<template>
  <v-card class="gallery-view">
    <gallery-view-toolbar
      :editable="editable"
      :event="event"
      :sensorType="sensorType"
      :canDownload="!event.updating"
      :downloadOptions="downloadOptions"
      :title="title"
      @download="download"
      @update="(attr) => $emit('updateEvent', attr)"
      @close="$emit('close')"
    >
      <toolbar-button
        icon="fa-trash"
        @click.native.stop="$emit('deleteEvent')"
        v-if="editable && !event.updating"
      >
        {{ $t('menu.delete') }}
      </toolbar-button>
    </gallery-view-toolbar>
    <layout
      class="mb-3"
      :hasPrevious="hasPrevious"
      :hasNext="hasNext"
      @previous="$emit('previous')"
      @next="$emit('next')"
    >
      <template #top>
        <gallery-view-header
          :editable="editable"
          :event="event"
          @updateEvent="$emit('updateEvent', $event)"
        />
      </template>
      <div class="gallery-view__image">
        <picked-insects-draw
          :imageSrc="event.attachments['display-photo']"
          :detectionsUrl="detectionsUrl"
          :hideCanvas="viewOriginal"
          :pickedDetectionType="pickedDetectionType"
          :originalImageWidth='ORIGINAL_IMAGE_WIDTH'
          :originalImageHeight='ORIGINAL_IMAGE_HEIGHT'
          @save="save"
          ref="drawer"
        />
      </div>
      <div class="d-flex justify-center">
        <replacement-notification
          class="mt-3"
          :trap_replaced_on="trap_replaced_on"
          :trap_effective_days="trap_effective_days"
        />
      </div>
      <template #side>
        <div class="result mb-2">
          <v-row no-gutters align="center" justify="end">
            <v-checkbox
              :label="$t('field.original')"
              v-model="viewOriginal"
              hide-details
              color="primary"
              class="mt-0 ml-3 mr-auto"
            />
            <chart-type-select v-model="chartType" />
          </v-row>
          <insect-count-table
            v-if="chartType == 'table'"
            :insectCounts="event.insect_counts"
            :showRadiobutton="!event.updating"
            :pickedDetectionType.sync="pickedDetectionType"
            showFocusButton
            tableMaxHeight="65vh"
            @click:focus="handleFocus"
          />
          <insect-count-by-type-bar-chart
            :insectCounts="event.insect_counts"
            v-if="chartType == 'bar'"
          />
          <top-5-insect-types-pie-chart
            :insectCounts="event.insect_counts"
            v-if="chartType == 'pie'"
          />
        </div>
        <div class="result pa-2">
          <div class="pt-2 px-2">
            <div class="result-item">
              <span>{{ $t('field.total') }}</span>
              <span class="mx-1">:</span>
              <span>{{ event.count }}</span>
            </div>
            <insect-count-difference class="result-item" :event="event" />
          </div>
          <hr />
          <threshold-alert-status
            class="pa-2"
            :event="event"
            :editable="editable"
            @updateThreshold="$emit('updateThreshold', $event)"
          />
        </div>
      </template>
    </layout>
    <slot name="thumbnails" />
  </v-card>
</template>

<i18n lang="yaml">
ja:
  menu:
    delete: 削除
  field:
    original: 撮影画像を表示
    total: 頭数合計
  msg:
    download:
      failed: 画像のダウンロードに失敗しました。

en:
  menu:
    delete: Delete
  field:
    original: Show original photo
    total: Total
  msg:
    download:
      failed: Failed to download image.
</i18n>

<script>
import Notifications from '@/mixins/notifications';
import TrainingDataDownload from '@/mixins/trainingDataDownload';

import ToolbarButton from '@/components/atoms/ToolbarButton';
import ChartTypeSelect from '@/components/atoms/ChartTypeSelect';
import InsectCountByTypeBarChart from '@/components/organisms/InsectCountByTypeBarChart';
import Top5InsectTypesPieChart from '@/components/organisms/Top5InsectTypesPieChart';
import GalleryViewHeader from '@/components/GalleryView/GalleryViewHeader';
import GalleryViewToolbar from '@/components/GalleryView/Toolbar';
import Layout from '@/components/GalleryView/Layout';
import InsectCountDifference from '@/components/GalleryView/InsectCountDifference';
import ReplacementNotification from '@/components/GalleryView/FlyCountView/ReplacementNotification';
import ThresholdAlertStatus from '@/components/GalleryView/FlyCountView/ThresholdAlertStatus';
import InsectCountTable from '@/components/InsectCountTable';
import PickedInsectsDraw from '@/components/PickedInsectsDraw';
import { ZoomProvider } from '@/components/Zoom';

const ORIGINAL_IMAGE_WIDTH = 50248;
const ORIGINAL_IMAGE_HEIGHT = 5472;

export default {
  name: 'FlyView',
  mixins: [
    Notifications,
    TrainingDataDownload,
    ZoomProvider,
  ],
  props: {
    editable: Boolean,
    event: Object,
    sensorType: String,
    hasPrevious: Boolean,
    hasNext: Boolean,
    title: String,
  },
  components: {
    ChartTypeSelect,
    GalleryViewHeader,
    GalleryViewToolbar,
    InsectCountByTypeBarChart,
    InsectCountDifference,
    InsectCountTable,
    Layout,
    PickedInsectsDraw,
    ReplacementNotification,
    ThresholdAlertStatus,
    ToolbarButton,
    Top5InsectTypesPieChart,
  },
  computed: {
    detectionsUrl() {
      return `/api/v2/events/${this.event.id}/insect_detections/photo`;
    },
    difference() {
      return this.event.count_difference;
    },
  },
  methods: {
    handleFocus() {
      this.$refs.drawer.focusNext();
    },
    download: async function (type) {
      switch (type) {
        case 'trainingData': {
          this.downloadAttachment(this.event.id, 'photo');
          break;
        }
        default: {
          this.save(this.$refs.drawer);
          break;
        }
      }
    },
    save: async function (canvas) {
      try {
        await canvas.export('download.png');
      } catch (error) {
        this.notifyError('msg.download.failed');
      }
    },
  },
  data() {
    return {
      viewOriginal: false,
      chartType: 'table',
      pickedDetectionType: 'all',
      ORIGINAL_IMAGE_WIDTH,
      ORIGINAL_IMAGE_HEIGHT,
    };
  },
  inject: [
    'downloadAttachment',
    'trap_effective_days',
    'trap_replaced_on',
  ],
  filters: {
    sign(value) {
      if (value > 0) {
        return `+ ${value}`;
      }
      if (value < 0) {
        return `- ${Math.abs(value)}`;
      }
      return '± 0';
    },
  },
  watch: {
    event(event, prev) {
      this.viewOriginal = false;
      if (event.id === prev.id) {
        this.$refs.drawer.refetch();
      } else {
        this.resetZoom();
      }
    },
  },
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'

.result
  border: 2px solid #ccc

  &-item,
  ::v-deep .threshold-alert-status__field
    font-size: 15px
    font-weight: 500
    margin-bottom: 0.5rem
    display: flex
    align-items: center

.gallery-view__image
  height: 65vh
</style>
